import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Table,
  Tag,
  Checkbox,
  Space,
  Alert,
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  App,
  Input,
} from 'antd';
import {
  SyncOutlined,
  CloudUploadOutlined,
  DatabaseOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FolderOpenOutlined,
  MobileOutlined,
  RocketOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import {
  adminAPI,
  BundledGameItem,
  BundledGamesStatusResponse,
  BundledGamesSyncResponse,
} from '../services/adminAPI';

const { Text, Paragraph } = Typography;

interface SyncBundledGamesModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const SyncBundledGamesModal: React.FC<SyncBundledGamesModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [statusData, setStatusData] = useState<BundledGamesStatusResponse['data'] | null>(null);
  const [seedDb, setSeedDb] = useState(true);
  const [uploadStorage, setUploadStorage] = useState(true);
  const [syncResult, setSyncResult] = useState<BundledGamesSyncResponse['data'] | null>(null);
  const [frontendBaseUrl, setFrontendBaseUrl] = useState<string>(() => {
    return localStorage.getItem('bundled_games_frontend_url') || '';
  });

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getBundledGamesStatus();
      if (res.success) {
        setStatusData(res.data);
        if (!frontendBaseUrl && res.data.detectedFrontendUrl) {
          setFrontendBaseUrl(res.data.detectedFrontendUrl);
        }
      }
    } catch (err: any) {
      console.error('Failed to load bundled games status:', err);
      message.error(err.response?.data?.message || 'Failed to check bundled games status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      setSyncResult(null);
      fetchStatus();
    }
  }, [visible]);

  const handleSync = async () => {
    if (!seedDb && !uploadStorage) {
      message.warning('Please select at least one synchronization action.');
      return;
    }

    try {
      setSyncing(true);
      setSyncResult(null);
      if (frontendBaseUrl.trim()) {
        try {
          localStorage.setItem('bundled_games_frontend_url', frontendBaseUrl.trim());
        } catch { }
      }

      const res = await adminAPI.syncBundledGames({
        seedDb,
        uploadStorage,
        frontendBaseUrl: frontendBaseUrl.trim() || undefined,
      });

      message.destroy('syncBundled');

      if (res.data?.success || res.data?.database?.success) {
        if (res.data?.storage && !res.data?.storage?.success) {
          message.warning({
            content: res.data.message || res.message,
            duration: 6,
          });
        } else {
          message.success(res.data.message || res.message || 'Bundled games synchronized successfully!');
        }
        setSyncResult(res.data);
        await fetchStatus();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        message.error(res.data?.message || res.message || 'Synchronization failed');
      }
    } catch (err: any) {
      message.destroy('syncBundled');
      console.error('Sync bundled games error:', err);
      message.error(err.response?.data?.message || err.message || 'Failed to synchronize bundled games');
    } finally {
      setSyncing(false);
    }
  };

  const columns = [
    {
      title: 'Icon',
      key: 'icon',
      width: 55,
      render: (_: any, record: BundledGameItem) => {
        const previewUrl =
          record.iconUrl ||
          (frontendBaseUrl
            ? `${frontendBaseUrl.replace(/\/+$/, '')}/games/${record.slug}/icon.png`
            : null);
        return previewUrl ? (
          <img
            src={previewUrl}
            alt={record.name}
            style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover' }}
            onError={(e) => {
              (e.target as HTMLElement).style.display = 'none';
            }}
          />
        ) : (
          <div
            style={{
              width: 32,
              height: 32,
              background: '#f0f0f0',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
            }}
          >
            🎮
          </div>
        );
      },
    },
    {
      title: 'Game',
      key: 'name',
      render: (_: any, record: BundledGameItem) => (
        <Space direction="vertical" size={2}>
          <Text strong>{record.name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            slug: <code>{record.slug}</code>
          </Text>
          {record.gameUrl && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              <code>{record.gameUrl}</code>
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'categorySlug',
      key: 'categorySlug',
      render: (cat: string) => <Tag color="blue">{cat}</Tag>,
    },
    {
      title: 'Local Package',
      key: 'local',
      render: (_: any, record: BundledGameItem) =>
        record.localFolderExists ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Ready (/public/games)
          </Tag>
        ) : (
          <Tag color="error" icon={<ExclamationCircleOutlined />}>
            Missing
          </Tag>
        ),
    },
    {
      title: 'Database Status',
      key: 'inDatabase',
      render: (_: any, record: BundledGameItem) =>
        record.inDatabase ? (
          <Tag color="cyan" icon={<CheckCircleOutlined />}>
            {record.status || 'APPROVED'}
          </Tag>
        ) : (
          <Tag color="warning" icon={<ExclamationCircleOutlined />}>
            Not Seeded
          </Tag>
        ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <MobileOutlined style={{ color: '#1677ff' }} />
          <span>Synchronize Mobile Bundled Games (11 Default Games)</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="close" onClick={onClose} disabled={syncing}>
          Close
        </Button>,
        <Button
          key="refresh"
          icon={<SyncOutlined />}
          onClick={fetchStatus}
          loading={loading}
          disabled={syncing}
        >
          Refresh Status
        </Button>,
        <Button
          key="sync"
          type="primary"
          icon={<RocketOutlined />}
          onClick={handleSync}
          loading={syncing}
          disabled={loading || (!seedDb && !uploadStorage)}
        >
          {syncing ? 'Synchronizing...' : 'Start Synchronization'}
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          These 11 games are pre-packaged directly in the mobile app bundle (<code>frontend/public/games/</code>)
          for instant offline play. This tool synchronizes their metadata to the PostgreSQL database and uploads their
          full package to Cloudflare R2 / S3 so they can also be played seamlessly on Web browsers and CDN.
        </Paragraph>

        {statusData && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Card size="small" style={{ textAlign: 'center', background: '#f6ffed', borderColor: '#b7eb8f' }}>
                <Statistic
                  title="Total Bundled Games"
                  value={statusData.totalBundled}
                  prefix={<MobileOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ textAlign: 'center', background: '#e6f4ff', borderColor: '#91caff' }}>
                <Statistic
                  title="Synced in Database"
                  value={statusData.syncedInDb}
                  suffix={`/ ${statusData.totalBundled}`}
                  prefix={<DatabaseOutlined />}
                  valueStyle={{ color: '#1677ff' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ textAlign: 'center', background: '#f9f0ff', borderColor: '#d3adf7' }}>
                <Statistic
                  title="Local Bundle Files"
                  value={statusData.gamesDirectoryExists ? 'Found' : 'Missing'}
                  prefix={<FolderOpenOutlined />}
                  valueStyle={{ color: statusData.gamesDirectoryExists ? '#722ed1' : '#cf1322' }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {statusData && !statusData.gamesDirectoryExists && (
          <Alert
            message="Notice: Game files not detected in API environment"
            description="The API server cannot locate local game files. Database metadata can still be seeded, but Storage upload requires files in api/bundled-games or setting the BUNDLED_GAMES_DIR environment variable."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Card
          size="small"
          title={
            <Space>
              <GlobalOutlined style={{ color: '#1677ff' }} />
              <span>Frontend Web Domain & CDN URL Configuration</span>
            </Space>
          }
          style={{ marginBottom: 16, backgroundColor: '#f0f7ff', borderColor: '#adc6ff' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size={6}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Enter your frontend web domain (e.g. <code>https://iwin.me</code>) or CDN URL.
              This allows the Admin tool, CDN, and Web to generate full absolute URLs for game icons and packages (e.g. <code>https://iwin.me/games/2048/icon.png</code>).
            </Text>
            <Input
              prefix={<GlobalOutlined style={{ color: '#8c8c8c' }} />}
              placeholder="e.g. https://iwin.me"
              value={frontendBaseUrl}
              onChange={(e) => setFrontendBaseUrl(e.target.value)}
              disabled={syncing}
              addonAfter={
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  onClick={() => setFrontendBaseUrl('https://iwin.me')}
                >
                  Use https://iwin.me
                </Button>
              }
            />
            {frontendBaseUrl && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                Preview Icon URL: <code>{frontendBaseUrl.replace(/\/+$/, '')}/games/2048/icon.png</code>
              </Text>
            )}
          </Space>
        </Card>

        <Card size="small" title="Sync Actions" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Checkbox
              checked={seedDb}
              onChange={(e) => setSeedDb(e.target.checked)}
              disabled={syncing}
            >
              <Space>
                <DatabaseOutlined />
                <Text strong>Upsert metadata in Database</Text>
                <Text type="secondary">(Title, slug, categories, orientation, CDN URLs)</Text>
              </Space>
            </Checkbox>
            <Checkbox
              checked={uploadStorage}
              onChange={(e) => setUploadStorage(e.target.checked)}
              disabled={syncing}
            >
              <Space>
                <CloudUploadOutlined />
                <Text strong>Upload packages to Active Storage (S3 / Cloudflare R2)</Text>
                <Text type="secondary">(HTML5 files, assets, audio, icons for Web players)</Text>
              </Space>
            </Checkbox>
          </Space>
        </Card>

        {syncResult && (
          <Alert
            message={syncResult.success ? "Synchronization Completed" : (syncResult.database?.success ? "Partial Synchronization" : "Synchronization Notice")}
            description={
              <div>
                {syncResult.database && (
                  <p style={{ margin: '4px 0' }}>
                    <DatabaseOutlined /> <strong>Database:</strong> {syncResult.database.inserted} new games
                    inserted, {syncResult.database.updated} existing games updated. Total:{' '}
                    {syncResult.database.total} games.
                  </p>
                )}
                {syncResult.storage && (
                  syncResult.storage.success ? (
                    <p style={{ margin: '4px 0' }}>
                      <CloudUploadOutlined /> <strong>Storage:</strong> Uploaded {syncResult.storage.totalFiles}{' '}
                      files ({syncResult.storage.sizeMb}) across {syncResult.storage.gamesCount} games to bucket{' '}
                      <code>{syncResult.storage.bucketName}</code> ({syncResult.storage.providerName}).
                    </p>
                  ) : (
                    <p style={{ margin: '4px 0', color: '#d46b08' }}>
                      <ExclamationCircleOutlined /> <strong>Storage Upload:</strong> {syncResult.storage.message}
                    </p>
                  )
                )}
              </div>
            }
            type={syncResult.success ? 'success' : (syncResult.database?.success ? 'warning' : 'error')}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Table
          columns={columns}
          dataSource={statusData?.games || []}
          rowKey="slug"
          size="small"
          loading={loading}
          pagination={false}
          scroll={{ y: 260 }}
        />
      </div>
    </Modal>
  );
};
